export type CrewReportCatalogEntry = {
  name: string;
  path: string;
  description: string;
};

export type CrewReportCatalogGroup = {
  name: string;
  description: string;
  reports: CrewReportCatalogEntry[];
};

export const CREW_REPORT_CATALOG: CrewReportCatalogGroup[] = [
  {
    name: 'Operations',
    description: 'Onboard headcount, relief availability, and NR flags',
    reports: [
      {
        name: 'Onboard',
        path: '/crew/reports/operations/onboard',
        description: 'Currently onboard crew (active ONB assignments)',
      },
      {
        name: 'Available',
        path: '/crew/reports/operations/available',
        description: 'Relief pool availability (excluding not-recommended)',
      },
      {
        name: 'Not recommended',
        path: '/crew/reports/operations/not-recommended',
        description: 'Crew flagged as not recommended',
      },
    ],
  },
  {
    name: 'Overview',
    description: 'Fleet and vessel KPIs — each statistic is a separate view',
    reports: [
      {
        name: 'Certs expired',
        path: '/crew/reports/compliance/certs-expired',
        description: 'Certificates already past expiry',
      },
      {
        name: 'Certs expiring',
        path: '/crew/reports/compliance/certs-expiring',
        description: 'Certificates due within the selected window',
      },
      {
        name: 'Medicals expired',
        path: '/crew/reports/compliance/medicals-expired',
        description: 'Fitness / medicals past next-due date',
      },
      {
        name: 'Medicals due soon',
        path: '/crew/reports/compliance/medicals-due',
        description: 'Fitness / medicals due within the selected window',
      },
      {
        name: 'COE soon',
        path: '/crew/reports/compliance/coe-soon',
        description: 'Contract end of employment approaching',
      },
      {
        name: 'Intention overdue',
        path: '/crew/reports/compliance/intention-overdue',
        description: 'Sign-off intention past due',
      },
      {
        name: 'Nationalities',
        path: '/crew/reports/compliance/nationalities',
        description: 'Distinct nationalities currently onboard',
      },
      {
        name: 'Appraisals / NR set',
        path: '/crew/statistics/people-payroll/appraisals',
        description: 'Crew with appraisals or NR flag set',
      },
      {
        name: 'NR count',
        path: '/crew/statistics/people-payroll/not-recommended',
        description: 'Crew flagged as not recommended',
      },
      {
        name: 'Avg months onboard',
        path: '/crew/statistics/people-payroll/avg-months-onboard',
        description: 'Average months on current ONB tour',
      },
      {
        name: 'Contract wage total',
        path: '/crew/statistics/people-payroll/contract-wages',
        description: 'Sum of contract wages for period assignments',
      },
      {
        name: 'Allottees',
        path: '/crew/statistics/people-payroll/allottees',
        description: 'Count of standing allottees',
      },
      {
        name: 'Allottee amount',
        path: '/crew/statistics/people-payroll/allottee-amount',
        description: 'Total standing allottee amounts',
      },
      {
        name: 'Nationality mix',
        path: '/crew/statistics/mix/nationality',
        description: 'Onboard headcount by nationality',
      },
      {
        name: 'Rank mix',
        path: '/crew/statistics/mix/rank',
        description: 'Onboard headcount by rank',
      },
      {
        name: 'Certificate expiry buckets',
        path: '/crew/statistics/mix/cert-expiry',
        description: 'Certificate expiry timing buckets with detail list',
      },
    ],
  },
  {
    name: 'Compliance',
    description: 'Certificates, medicals, and manning mix',
    reports: [
      {
        name: 'Document Gaps',
        path: '/crew/cert-gaps',
        description: 'Required vs held/valid certificates by vessel, rank, and nationality',
      },
      {
        name: 'Certificate expiry',
        path: '/crew/certs-expiring',
        description: 'Certificates expiring or already expired within a chosen window',
      },
    ],
  },
  {
    name: 'Planning',
    description: 'Availability, relief, and contract end of employment',
    reports: [
      {
        name: 'Onboard Status (AI)',
        path: '/crew/onboard-status',
        description:
          'Onboard crewlist with dashboard glance fields plus AI strengths, risks, and recommendations',
      },
      {
        name: 'Relief board',
        path: '/crew/relief-board',
        description: 'Available shore crew for placement: dates, days ashore, last tour, appraisal, certs/medical',
      },
      {
        name: 'COE watchlist',
        path: '/crew/coe-watchlist',
        description: 'Months onboard, COE approaching, and overdue sign-off intention',
      },
    ],
  },
  {
    name: 'People',
    description: 'Appraisals, recommendations, and sea experience',
    reports: [
      {
        name: 'Appraisals / NR',
        path: '/crew/appraisal-nr',
        description: 'Last appraisal, overall evaluation, and not-recommended list',
      },
      {
        name: 'Previous service experience',
        path: '/crew/previous-service',
        description: 'Time in rank and vessel type for onboard crew',
      },
    ],
  },
  {
    name: 'Payroll-facing',
    description: 'Contract wages and standing allottees (period remittance stays in MGA)',
    reports: [
      {
        name: 'Wage / allottee',
        path: '/crew/wage-allottee',
        description: 'Contract wages and allottee totals by vessel and period',
      },
    ],
  },
  {
    name: 'Matrices & movements',
    description: 'Qualification matrices and crew change counts',
    reports: [
      {
        name: "Officer's Qualification Matrix",
        path: '/crew/officer-matrix',
        description: 'Officer experience and certificate matrix by vessel',
      },
      {
        name: 'RightShip Officers Qualifications Matrix',
        path: '/crew/rightship-matrix',
        description: 'RightShip-style officer qualifications matrix',
      },
      {
        name: 'Total Crew Changes per Vessel',
        path: '/crew/changes-per-vessel',
        description: 'Crew change counts by vessel over a period',
      },
    ],
  },
];

export function flattenCrewReportCatalogForSearch(): { label: string; path: string; group: string }[] {
  const out: { label: string; path: string; group: string }[] = [];
  for (const group of CREW_REPORT_CATALOG) {
    for (const report of group.reports) {
      out.push({
        label: report.name,
        path: report.path,
        group: `Crew › Reports › ${group.name}`,
      });
    }
  }
  return out;
}
